import { getContainedBy, getIndexForNode } from "@/common";
import AttributeValue from "@imminently/decisively-ui/AttributeValue";
import Button from "@imminently/decisively-ui/Button";
import Chip from "@imminently/decisively-ui/Chip";
import Icon from "@imminently/decisively-ui/Icon";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import Typography from "@mui/material/Typography";
import clsx from "clsx";
import React from "react";

const ReportNode = (props) => {
  const {
    node: { children, node, type, title },
    expanded,
    isSearchMatch,
    isSearchFocus,
    toggleChildrenVisibility,
    graph,
  } = props;

  if (type === "special") {
    return (
      <div className="flex cursor-pointer justify-between pl-0">
        <Typography style={{ opacity: 0.7 }}>{title}</Typography>
      </div>
    );
  }

  if (!node) return null;
  const hasAtLeastOneNonHiddenChild = React.useMemo(
    () => (Array.isArray(children) ? children : []).some((it) => it.node?.hidden !== true),
    [children],
  );
  const showExpandLess = expanded || hasAtLeastOneNonHiddenChild === false;
  const value = typeof node.derived === "undefined" ? node.input : node.derived;
  let isUnknownOrUncertain = false;
  let status;
  if (typeof value === "undefined") {
    status = "Unknown";
    isUnknownOrUncertain = true;
  } else if (value === null) {
    status = "Uncertain";
    isUnknownOrUncertain = true;
  } else if (value === false) status = "Not satisfied";
  else if (value.temporal) {
    let atleastOneTrue = false;
    let atleastOneFalse = false;
    for (const r of value.temporal.ranges) {
      if (r.v === false) {
        atleastOneFalse = true;
      } else if (typeof r.v !== "undefined" && r.v !== null) {
        atleastOneTrue = true;
      }
    }
    if (atleastOneTrue) status = "Satisfied at times";
    else if (atleastOneFalse) status = "Not satisfied at times";
    else {
      status = "Uncertain at times";
      isUnknownOrUncertain = true;
    }
  } else status = "Satisfied";
  const statusVariant = React.useMemo(() => {
    if (status === "Satisfied" || status === "Satisfied at times") return "success";
    if (status === "Not satisfied" || status === "Not satisfied at times") return "destructive";
    if (status === "Unknown" || status === "Uncertain" || status === "Uncertain at times") return "subtle";
    return "secondary";
  }, [status]);

  const toggleExpanded = React.useCallback(() => {
    if (!toggleChildrenVisibility || hasAtLeastOneNonHiddenChild === false) return;
    return toggleChildrenVisibility(props);
  }, [props, toggleChildrenVisibility, hasAtLeastOneNonHiddenChild]);

  const indexInfo = getIndexForNode(node, graph);

  const tooltipContent = (
    <>
      <b>{node.description}</b>
      <div>Entity: {node.entity}</div>
      {node.index && <div>Index: {node.index}</div>}
      {indexInfo?.hint && <div>Identifier: {indexInfo.value}</div>}
      {node.path && getContainedBy(node.path, graph)}
    </>
  );

  return (
    <div
      className={clsx(
        "flex cursor-pointer items-start gap-2 rounded-lg border-2 p-1.5 hover:bg-secondary-hover",
        isSearchMatch ? "border-muted" : "border-transparent",
        isSearchMatch && isSearchFocus && "bg-primary-subtle",
        isUnknownOrUncertain && "text-[grey]",
      )}
    >
      <Button
        icon
        className="p-0"
        label={showExpandLess ? "Collapse" : "Expand"}
        disabled={children.length === 0 || hasAtLeastOneNonHiddenChild === false}
        onClick={(event) => {
          event.stopPropagation();
          toggleExpanded();
        }}
      >
        {children.length > 0 ? (
          showExpandLess ? (
            <Icon name="collapse" />
          ) : (
            <Icon name="expand" />
          )
        ) : (
          <Icon name="minus" />
        )}
      </Button>
      <div
        className="flex flex-col gap-1"
      >
        <Tooltip
          interactable={true}
          enterDelay={500}
          placement="bottom-start"
        >
          <Tooltip.Trigger className="flex gap-2">
            <Typography className="[font-family:'Libre_Baskerville'] whitespace-nowrap">{node.description}</Typography>
            {isUnknownOrUncertain ? null : (
              <Chip
                variant={statusVariant}
                size="sm"
              >
                {status}
              </Chip>
            )}
          </Tooltip.Trigger>
          <Tooltip.Content className="max-w-[300px]">
            {tooltipContent}
          </Tooltip.Content>
        </Tooltip>
        <div className="flex items-center gap-1">
          <Typography
            variant="h6"
            className="font-bold"
          >
            Value:
          </Typography>
          <div
            onClick={e => e.stopPropagation()}>
              <AttributeValue  temporalChartHeight={52} node={node} />

          </div>
        </div>
      </div>
    </div>
  );
};
export default ReportNode;
